/**
 * AiSessionWizard — Step-by-step wizard for AI simulation mode
 *
 * Three-step flow:
 *   Step 1: Choose session type, view assembled prompt, copy to clipboard
 *   Step 2: Paste AI response back
 *   Step 3: Review parsed results (confidence, questions, evidence recommendations)
 *
 * Also supports viewing completed sessions (read-only mode).
 */

import React, { useState, useCallback, useEffect } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { WorkspaceResponse } from "../../api/workspaces";
import {
  assemblePrompt,
  createAiSession,
  completeAiSession,
  fetchAiSession,
  fetchPromptTemplates,
  fetchClarifyingQuestions,
  createClarifyingQuestion,
} from "../../api/workspaceAi";
import type {
  AssembledPromptResponse,
  PromptTemplateResponse,
  AiSessionResponse,
  ClarifyingQuestionResponse,
} from "../../api/workspaceAi";
import styles from "./AiSessionWizard.module.css";

interface Props {
  workspace: WorkspaceResponse;
  /** If set, opens in view mode for an existing session */
  sessionGuid: string | null;
  /** If set, auto-selects this session type and jumps to the prompt step */
  initialSessionType?: string;
  onClose: () => void;
}

type WizardStep = "choose" | "prompt" | "paste" | "review";

const AiSessionWizard: React.FC<Props> = ({
  workspace,
  sessionGuid,
  initialSessionType,
  onClose,
}) => {
  const { t } = useAppTranslation("copilot");

  // Wizard state
  const [step, setStep] = useState<WizardStep>(
    sessionGuid ? "review" : "choose"
  );
  const [templates, setTemplates] = useState<PromptTemplateResponse[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [assembledPromptData, setAssembledPromptData] =
    useState<AssembledPromptResponse | null>(null);
  const [session, setSession] = useState<AiSessionResponse | null>(null);
  const [pastedResponse, setPastedResponse] = useState("");
  const [confidenceScore, setConfidenceScore] = useState("");
  const [confidenceRationale, setConfidenceRationale] = useState("");
  const [questions, setQuestions] = useState<ClarifyingQuestionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Load templates on mount
  useEffect(() => {
    async function loadTemplates() {
      try {
        const tpls = await fetchPromptTemplates(workspace.workspaceGuid);
        setTemplates(tpls);
        if (tpls.length > 0 && !selectedType) {
          setSelectedType(initialSessionType ?? tpls[0].sessionType);
        }
      } catch {
        // templates may not be seeded yet
      }
    }
    if (!sessionGuid) {
      loadTemplates();
    }
  }, [workspace.workspaceGuid, sessionGuid, selectedType, initialSessionType]);

  // Auto-assemble prompt when initialSessionType is provided (skip "choose" step)
  const autoAssembledRef = React.useRef(false);
  useEffect(() => {
    if (!initialSessionType || sessionGuid || autoAssembledRef.current) return;
    if (!selectedType || selectedType !== initialSessionType) return;

    autoAssembledRef.current = true;
    async function autoAssemble() {
      setLoading(true);
      setError(null);
      try {
        const assembled = await assemblePrompt(
          workspace.workspaceGuid,
          initialSessionType!
        );
        setAssembledPromptData(assembled);
        setStep("prompt");
      } catch (e: any) {
        setError(e?.message ?? "Failed to assemble prompt.");
      } finally {
        setLoading(false);
      }
    }
    autoAssemble();
  }, [initialSessionType, sessionGuid, selectedType, workspace.workspaceGuid]);

  // Load existing session if viewing
  useEffect(() => {
    async function loadSession() {
      if (!sessionGuid) return;
      setLoading(true);
      try {
        const s = await fetchAiSession(
          workspace.workspaceGuid,
          sessionGuid
        );
        setSession(s);
        setPastedResponse(s.rawOutput ?? "");
        if (s.confidenceScore != null) {
          setConfidenceScore(String(Math.round(s.confidenceScore * 100)));
        }
        setConfidenceRationale(s.confidenceRationale ?? "");

        // Load questions for this session
        const qs = await fetchClarifyingQuestions(
          workspace.workspaceGuid,
          sessionGuid
        );
        setQuestions(qs);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load session.");
      } finally {
        setLoading(false);
      }
    }
    loadSession();
  }, [workspace.workspaceGuid, sessionGuid]);

  // Step 1 → Step 2: Assemble prompt
  const handleAssemblePrompt = useCallback(async () => {
    if (!selectedType) return;
    setLoading(true);
    setError(null);
    try {
      const assembled = await assemblePrompt(
        workspace.workspaceGuid,
        selectedType
      );
      setAssembledPromptData(assembled);
      setStep("prompt");
    } catch (e: any) {
      setError(
        e?.message ?? t("wizard.assembleError")
      );
    } finally {
      setLoading(false);
    }
  }, [workspace.workspaceGuid, selectedType, t]);

  // Copy prompt to clipboard
  const handleCopyPrompt = useCallback(async () => {
    if (!assembledPromptData) return;
    try {
      await navigator.clipboard.writeText(
        assembledPromptData.combinedPrompt
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback: select text
    }
  }, [assembledPromptData]);

  // Step 2 → Step 3: Create session and submit response
  const handleSubmitResponse = useCallback(async () => {
    if (!pastedResponse.trim()) return;
    setLoading(true);
    setError(null);
    try {
      // Create the session
      const newSession = await createAiSession(workspace.workspaceGuid, {
        sessionType: selectedType,
        modelId: "manual",
      });

      // Parse confidence from user input
      const score = confidenceScore
        ? Math.min(1, Math.max(0, parseFloat(confidenceScore) / 100))
        : undefined;

      // Try to parse the pasted response as JSON for structured outputPayload.
      // In simulation mode, the AI response is often valid JSON that contains
      // structured fields (presenting_concern_narrative, evidence_items, etc.).
      // If parsing fails, we still send it as rawOutput only.
      let parsedPayload: string | undefined;
      try {
        const parsed = JSON.parse(pastedResponse.trim());
        if (typeof parsed === "object" && parsed !== null) {
          parsedPayload = JSON.stringify(parsed);
        }
      } catch {
        // Response isn't JSON — that's fine, rawOutput will be used
      }

      // Complete with the pasted response
      const completed = await completeAiSession(
        workspace.workspaceGuid,
        newSession.sessionGuid,
        {
          rawOutput: pastedResponse.trim(),
          outputPayload: parsedPayload,
          confidenceScore: score,
          confidenceRationale: confidenceRationale || undefined,
        }
      );

      setSession(completed);

      // Try to parse clarifying questions from the response
      try {
        const parsed = JSON.parse(pastedResponse.trim());
        if (parsed.clarifying_questions && Array.isArray(parsed.clarifying_questions)) {
          for (let i = 0; i < parsed.clarifying_questions.length; i++) {
            const q = parsed.clarifying_questions[i];
            const text = typeof q === "string" ? q : q.question || q.text || "";
            if (text) {
              await createClarifyingQuestion(
                workspace.workspaceGuid,
                completed.sessionGuid,
                { questionText: text, sortOrder: i }
              );
            }
          }
          // Reload questions
          const qs = await fetchClarifyingQuestions(
            workspace.workspaceGuid,
            completed.sessionGuid
          );
          setQuestions(qs);
        }
      } catch {
        // Response isn't JSON or doesn't have questions — that's fine
      }

      setStep("review");
    } catch (e: any) {
      setError(e?.message ?? t("wizard.submitError"));
    } finally {
      setLoading(false);
    }
  }, [
    workspace.workspaceGuid,
    selectedType,
    pastedResponse,
    confidenceScore,
    confidenceRationale,
    t,
  ]);

  const isViewMode = !!sessionGuid;

  return (
    <div className={styles.wizard}>
      {/* Wizard header with step indicator */}
      <div className={styles.wizardHeader}>
        <button
          type="button"
          className={styles.wizardBackBtn}
          onClick={onClose}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {t("wizard.backToPanel")}
        </button>

        {!isViewMode && (
          <div className={styles.stepIndicator}>
            <StepDot
              active={step === "choose"}
              complete={step !== "choose"}
              label="1"
            />
            <div className={styles.stepLine} />
            <StepDot
              active={step === "prompt"}
              complete={step === "paste" || step === "review"}
              label="2"
            />
            <div className={styles.stepLine} />
            <StepDot
              active={step === "paste"}
              complete={step === "review"}
              label="3"
            />
            <div className={styles.stepLine} />
            <StepDot active={step === "review"} complete={false} label="4" />
          </div>
        )}
      </div>

      {error && (
        <div className={styles.wizardError}>{error}</div>
      )}

      {/* ── Step 1: Choose session type ── */}
      {step === "choose" && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>
            {t("wizard.chooseTitle")}
          </h3>
          <p className={styles.stepDesc}>
            {t("wizard.chooseDesc")}
          </p>

          {templates.length === 0 ? (
            <div className={styles.noTemplates}>
              {t("wizard.noTemplates")}
            </div>
          ) : (
            <div className={styles.templateGrid}>
              {templates.map((tpl) => (
                <button
                  key={tpl.templateGuid}
                  type="button"
                  className={`${styles.templateCard} ${
                    selectedType === tpl.sessionType
                      ? styles.templateCardSelected
                      : ""
                  }`}
                  onClick={() => setSelectedType(tpl.sessionType)}
                >
                  <span className={styles.templateName}>
                    {tpl.templateName}
                  </span>
                  {tpl.description && (
                    <span className={styles.templateDesc}>
                      {tpl.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleAssemblePrompt}
              disabled={!selectedType || loading}
            >
              {loading
                ? t("wizard.assembling")
                : t("wizard.assemblePrompt")}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: View & copy assembled prompt ── */}
      {step === "prompt" && assembledPromptData && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>
            {t("wizard.promptTitle")}
          </h3>
          <p className={styles.stepDesc}>
            {t("wizard.promptDesc")}
          </p>

          <div className={styles.promptBlock}>
            <div className={styles.promptBlockHeader}>
              <span className={styles.promptBlockLabel}>
                {assembledPromptData.templateName}
              </span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopyPrompt}
              >
                {copied
                  ? t("wizard.copied")
                  : t("wizard.copyPrompt")}
              </button>
            </div>
            <pre className={styles.promptText}>
              {assembledPromptData.combinedPrompt}
            </pre>
          </div>

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setStep("choose")}
            >
              {t("wizard.back")}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => setStep("paste")}
            >
              {t("wizard.nextPaste")}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Paste AI response ── */}
      {step === "paste" && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>
            {t("wizard.pasteTitle")}
          </h3>
          <p className={styles.stepDesc}>
            {t("wizard.pasteDesc")}
          </p>

          <textarea
            className={styles.pasteArea}
            rows={12}
            value={pastedResponse}
            onChange={(e) => setPastedResponse(e.target.value)}
            placeholder={t("wizard.pastePlaceholder")}
            disabled={loading}
          />

          <div className={styles.confidenceRow}>
            <div className={styles.confidenceField}>
              <label className={styles.fieldLabel}>
                {t("wizard.confidenceLabel")}
              </label>
              <input
                type="number"
                className={styles.confidenceInput}
                value={confidenceScore}
                onChange={(e) => setConfidenceScore(e.target.value)}
                placeholder="0-100"
                min="0"
                max="100"
                disabled={loading}
              />
            </div>
            <div className={styles.confidenceField} style={{ flex: 1 }}>
              <label className={styles.fieldLabel}>
                {t("wizard.confidenceRationale")}
              </label>
              <input
                type="text"
                className={styles.confidenceRationaleInput}
                value={confidenceRationale}
                onChange={(e) => setConfidenceRationale(e.target.value)}
                placeholder={t("wizard.rationalePlaceholder")}
                disabled={loading}
              />
            </div>
          </div>

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setStep("prompt")}
              disabled={loading}
            >
              {t("wizard.back")}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSubmitResponse}
              disabled={!pastedResponse.trim() || loading}
            >
              {loading
                ? t("wizard.submitting")
                : t("wizard.submitResponse")}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4 / View: Review results ── */}
      {step === "review" && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>
            {isViewMode
              ? t("wizard.viewTitle")
              : t("wizard.reviewTitle")}
          </h3>

          {loading ? (
            <div className={styles.loadingState}>
              {t("loading")}
            </div>
          ) : session ? (
            <>
              {/* Session metadata */}
              <div className={styles.reviewMeta}>
                <div className={styles.reviewMetaItem}>
                  <span className={styles.reviewMetaLabel}>
                    {t("wizard.sessionType")}
                  </span>
                  <span className={styles.reviewMetaValue}>
                    {t(
                      `sessionTypes.${session.sessionType}`
                    )}
                  </span>
                </div>
                <div className={styles.reviewMetaItem}>
                  <span className={styles.reviewMetaLabel}>
                    {t("wizard.status")}
                  </span>
                  <span className={styles.reviewMetaValue}>
                    {t(`status.${session.status}`)}
                  </span>
                </div>
                {session.confidenceScore != null && (
                  <div className={styles.reviewMetaItem}>
                    <span className={styles.reviewMetaLabel}>
                      {t("confidence")}
                    </span>
                    <span className={styles.confidenceBadge}>
                      {Math.round(session.confidenceScore * 100)}%
                    </span>
                  </div>
                )}
              </div>

              {/* AI Response */}
              {session.rawOutput && (
                <div className={styles.responseBlock}>
                  <span className={styles.responseLabel}>
                    {t("wizard.aiResponse")}
                  </span>
                  <pre className={styles.responseText}>
                    {session.rawOutput}
                  </pre>
                </div>
              )}

              {/* Clarifying questions generated */}
              {questions.length > 0 && (
                <div className={styles.reviewSection}>
                  <span className={styles.reviewSectionTitle}>
                    {t("wizard.generatedQuestions")}
                  </span>
                  <div className={styles.reviewQuestionsList}>
                    {questions.map((q, idx) => (
                      <div
                        key={q.questionGuid}
                        className={styles.reviewQuestion}
                      >
                        <span className={styles.reviewQuestionNum}>
                          {idx + 1}.
                        </span>
                        <span>{q.questionText}</span>
                        {q.answerText && (
                          <div className={styles.reviewAnswer}>
                            {q.answerText}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confidence disclaimer */}
              <div className={styles.disclaimer}>
                {t("wizard.confidenceDisclaimer")}
              </div>
            </>
          ) : null}

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onClose}
            >
              {t("wizard.done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Step dot indicator ──────────────────────────────────────

const StepDot: React.FC<{
  active: boolean;
  complete: boolean;
  label: string;
}> = ({ active, complete, label }) => (
  <span
    className={`${styles.stepDot} ${
      active ? styles.stepDotActive : complete ? styles.stepDotComplete : ""
    }`}
  >
    {complete ? "✓" : label}
  </span>
);

export default AiSessionWizard;
